import { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import { useGroupCommand } from "src/providers/GroupCommandProvider";
import { RootState } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectDevicesByGroupId } from "@dashboard/devices/store/selectors/devices";
import { Group } from "@dashboard/devices/types";
import { NewCommandDialog } from "@devices/components/Commands/NewCommandDialog";
import { MdmCommand } from "@devices/types/commands";
import { Box, TableRow } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { PaneElement } from "../../shared/PaneElement";

import GroupCommandItem from "./GroupCommandItem";

type CommandsProps = {
    group: Group;
};

const useStyles = makeStyles()((theme) => ({
    tooltipWrapper: {
        display: "block",
        height: "100%",
    },
    headerBox: { display: "flex", justifyContent: "space-between", paddingBottom: theme.spacing(1) },
    btnBox: { display: "flex", gap: theme.spacing(1) },
}));

const columnHelper = createColumnHelper<MdmCommand>();

const columns = [
    columnHelper.accessor("id", {
        id: "collapse",
        header: "",
        cell: () => null,
        enableSorting: false,
    }),
    columnHelper.accessor("created", {
        id: "date",
        header: i18next.t("common:label.date"),
        cell: (info) => info.getValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("status", {
        id: "status",
        header: i18next.t("common:label.status"),
        cell: (info) => info.getValue(),
        meta: { align: "center" },
        enableSorting: false,
    }),
    columnHelper.accessor("payload", {
        id: "command",
        header: i18next.t("sidepanels:groupPanel.table.header.command"),
        cell: (info) => info.getValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("id", {
        id: "actions",
        header: "",
        cell: () => null,
        enableSorting: false,
    }),
];

const CommandsTab = ({ group }: CommandsProps) => {
    const { t } = useTranslation(["sidepanels", "common", "modals"]);
    const { classes } = useStyles();
    const groupDevices = useSelector((state: RootState) => selectDevicesByGroupId(state, group.id));
    const [dialogOpen, toggleDialog] = useReducer((state) => !state, false);
    const commandsContext = useGroupCommand();

    const table = useReactTable({
        data: commandsContext.data,
        columns,
        state: {
            sorting: commandsContext.sorting,
            pagination: {
                pageIndex: commandsContext.pageIndex,
                pageSize: commandsContext.pageSize,
            },
        },
        onSortingChange: commandsContext.setSorting,
        pageCount: Math.ceil(commandsContext.totalCount / commandsContext.pageSize),
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handlePageChange = (_: unknown, newPage: number) => {
        commandsContext.setPageIndex(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        commandsContext.setPageSize(newPageSize);
        commandsContext.setPageIndex(0);
    };

    return (
        <PaneElement.Container>
            <Box className={classes.headerBox}>
                <PaneElement.Header>{t("sidepanels:groupPanel.commandsTab.title")}</PaneElement.Header>
                <SecondaryButton
                    data-testid="create-command-button"
                    onClick={toggleDialog}
                    disabled={!groupDevices.length}
                    tooltipProps={{
                        title: groupDevices.length
                            ? t("sidepanels:groupPanel.tooltip.createCommand")
                            : t("sidepanels:groupPanel.tooltip.noDevices"),
                    }}
                >
                    <i className="fas fa-solid fa-square-plus" />
                </SecondaryButton>
            </Box>

            <PaneElement.TableLayout
                contentDeps={[commandsContext.pageIndex, commandsContext.pageSize, commandsContext.data.length]}
                pagination={
                    <CustomTablePagination
                        component="div"
                        count={commandsContext.totalCount}
                        rowsPerPageOptions={commandsContext.rowsPerPage}
                        rowsPerPage={commandsContext.pageSize}
                        page={commandsContext.pageIndex}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton
                        showLastButton
                    />
                }
            >
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        loading={commandsContext.isLoading}
                        noDataMessage={t("sidepanels:groupPanel.table.noCommands")}
                    >
                        {table.getRowModel().rows.map((row, index) => (
                            <GroupCommandItem
                                key={row.original.id}
                                command={row.original}
                                index={index}
                                deleteCommand={commandsContext.onDelete}
                                createCommand={commandsContext.onCreate}
                                isCreating={commandsContext.resendingId === row.original.id}
                                isDeleting={commandsContext.deletingId === row.original.id}
                            />
                        ))}
                    </TableBody>
                </MinimalTable>
            </PaneElement.TableLayout>
            <NewCommandDialog
                title={t("modals:newCommand.title")}
                group={group}
                isOpen={dialogOpen}
                toggleDialog={toggleDialog}
                createCommand={commandsContext.onCreate}
            />
        </PaneElement.Container>
    );
};

export default CommandsTab;
